import "./GenericDateRangeFilter.css";
import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { EventIcon } from "../../../services/icons";

const INVALID_RANGE_MESSAGE = "End time cannot be earlier than start time";

const DateRangePicker = ({ onDateRangeSelect, initialStartDate, initialEndDate, isHourTime=false, label="Date Range" }) => {
  const MIN_YEAR = 2023;
  const MAX_YEAR = new Date().getFullYear();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const pickerRef = useRef(null);
  const dropdownRef = useRef(null);
  const startTimeRef = useRef(null);
  const endTimeRef = useRef(null);

  // UI state
  const [showPicker, setShowPicker] = useState(false);
  const [showQuickOptions, setShowQuickOptions] = useState(true);
  const [currentDate, setCurrentDate] = useState(() => {
    const base = initialStartDate ? new Date(initialStartDate) : new Date();
    return base > today ? new Date(today) : base;
  });

  // Selected values (Date objects). selectedStart/selectedEnd may include time.
  const [selectedStart, setSelectedStart] = useState(initialStartDate || null);
  const [selectedEnd, setSelectedEnd] = useState(initialEndDate || null);
  const [rangeError, setRangeError] = useState("");

  // Temporary hover while choosing range
  const [tempEndDate, setTempEndDate] = useState(null);

  // Flags: waiting for user to edit time inputs
  const [awaitingStartTime, setAwaitingStartTime] = useState(false);
  const [awaitingEndTime, setAwaitingEndTime] = useState(false);

  // Controlled time strings for the <input type="time">
  const [startTime, setStartTime] = useState(initialStartDate ? formatTime(initialStartDate) : "");
  const [endTime, setEndTime] = useState(initialEndDate ? formatTime(initialEndDate) : "");

  // ----------------- helper functions -----------------
  const toStartOfDay = (d) => (d ? new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0) : null);
  const toEndOfDay = (d) => d ? new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999) : null;
  const isSameDay = (a, b) =>!!a && !!b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  const isFutureDate = (date) => toStartOfDay(date) > today;

  function formatTime(date) {
    if (!date) return "";
    const h = String(date.getHours()).padStart(2, "0");
    const m = String(date.getMinutes()).padStart(2, "0");
    return `${h}:${m}`;
  }
  const formatDate = (date) => {
    if (!date) return "";
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };
  const formatDateTime = (date) => (date ? `${formatDate(date)} ${formatTime(date)}` : "");
  const isInvalidRange = (sDate, eDate) => Boolean(sDate && eDate && eDate < sDate);

  // ----------------- outside click (close only on outside click) -----------------
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setShowPicker(false);
        setAwaitingStartTime(false);
        setAwaitingEndTime(false);
      }
    };
    document.addEventListener("click", handleClickOutside, true);
    return () => document.removeEventListener("click", handleClickOutside, true);
  }, []);

  // when opening, jump to month containing start if available
  useEffect(() => {
    if (showPicker) {
      if (selectedStart) setCurrentDate(new Date(selectedStart));
      else {
        const base = new Date();
        setCurrentDate(base > today ? new Date(today) : base);
      }
    }
  }, [showPicker]); // eslint-disable-line

  useEffect(() => {
    if (showPicker && dropdownRef.current) {
      const dropdown = dropdownRef.current;
      dropdown.style.left = "";
      dropdown.style.right = "";

      const dialogPaper = dropdown.closest(".MuiDialog-paper");
      const boundaryRect = dialogPaper?.getBoundingClientRect();
      const boundaryLeft = boundaryRect ? boundaryRect.left + 8 : 8;
      const boundaryRight = boundaryRect ? boundaryRect.right - 8 : window.innerWidth - 8;
      const rect = dropdown.getBoundingClientRect();

      if (rect.right > boundaryRight) {
        dropdown.style.left = "auto";
        dropdown.style.right = "0";
      } else if (rect.left < boundaryLeft) {
        dropdown.style.right = "auto";
        dropdown.style.left = "0";
      }
    }
  }, [showPicker, showQuickOptions]);

  // focus time inputs after day selection
  useEffect(() => {
    if (awaitingStartTime && startTimeRef.current) startTimeRef.current.focus();
  }, [awaitingStartTime]);
  useEffect(() => {
    if (awaitingEndTime && endTimeRef.current) endTimeRef.current.focus();
  }, [awaitingEndTime]);

  // keep controlled time inputs in sync with selected dates unless user is about to type
  useEffect(() => {
    if (!selectedStart) return setStartTime("");
    if (!awaitingStartTime) setStartTime(formatTime(selectedStart));
  }, [selectedStart, awaitingStartTime]);
  useEffect(() => {
    if (!selectedEnd) return setEndTime("");
    if (!awaitingEndTime) setEndTime(formatTime(selectedEnd));
  }, [selectedEnd, awaitingEndTime]);

  // Generate days for a month
  const generateDaysArray = (year, month) => {
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDay = new Date(year, month, 1).getDay();
    return Array.from({ length: daysInMonth + firstDay }, (_, i) =>
      i < firstDay ? null : i - firstDay + 1
    );
  };

  const getSelectedRange = () => {
    const range = [];
    if (selectedStart && (selectedEnd || tempEndDate)) {
      const end = selectedEnd || tempEndDate;
      const current = new Date(toStartOfDay(selectedStart));
      const last = toStartOfDay(end);

      while (current <= last) {
        range.push(new Date(current));
        current.setDate(current.getDate() + 1);
      }
    }
    return range;
  };

  const selectedRange = getSelectedRange();

  // ----------------- Core selection logic -----------------
  // When both start & end days are selected, we will immediately call onDateRangeSelect
  // using start-of-day and end-of-day defaults so parent can enable submit.
  // Later, when the user selects times, we'll re-send with the updated times.
  const notifyParentRange = (sDate, eDate) => {
    if (!sDate || !eDate) return;
    if (isInvalidRange(sDate, eDate)) {
      setRangeError(INVALID_RANGE_MESSAGE);
      return;
    }
    setRangeError("");
    onDateRangeSelect({
      startDate: sDate,
      endDate: eDate,
      formattedStart: formatDateTime(sDate),
      formattedEnd: formatDateTime(eDate),
    });
  };

  const handleDateSelect = (date) => {
    if (isFutureDate(date)) return;
    const pickedStartOfDay = toStartOfDay(date);

    // If starting a new range (no start, or both already set), set start and wait for time or end day
    if (!selectedStart || (selectedStart && selectedEnd)) {
      setSelectedStart(pickedStartOfDay);
      setStartTime(""); // blank time until user chooses
      setAwaitingStartTime(true);
      setSelectedEnd(null);
      setEndTime("");
      setAwaitingEndTime(false);
      setTempEndDate(null);
      setRangeError("");
      setShowQuickOptions(false);
      return;
    }

    // If clicked a day earlier than current start -> treat as new start
    if (pickedStartOfDay < toStartOfDay(selectedStart)) {
      setSelectedStart(pickedStartOfDay);
      setStartTime("");
      setAwaitingStartTime(true);
      setSelectedEnd(null);
      setEndTime("");
      setAwaitingEndTime(false);
      setTempEndDate(null);
      setRangeError("");
      return;
    }

    // Otherwise select end day (we store end with default end-of-day)
    const endDefault = toEndOfDay(date);
    setSelectedEnd(endDefault);
    setEndTime(""); // user can set time; we will still notify parent with default times so Submit enables
    setAwaitingEndTime(true);
    setTempEndDate(null);

    // Notify parent immediately with default start-of-day / end-of-day so Submit becomes enabled
    const s = selectedStart ? (awaitingStartTime ? toStartOfDay(selectedStart) : selectedStart) : toStartOfDay(date);
    // If selectedStart exists but had time not yet set, use start-of-day as default for now
    notifyParentRange(s, endDefault);
  };

  const handleDateHover = (date) => {
    if (selectedStart && !selectedEnd && !isFutureDate(date)) {
      setTempEndDate(toStartOfDay(date));
    }
  };

  // ----------------- Time handling -----------------
  // When user changes the time, update selectedStart/selectedEnd and re-send to parent
  const handleTimeChange = (type, value) => {
    const [hours, minutes] = value.split(":").map(Number);

    if (type === "start" && selectedStart) {
      const updated = new Date(selectedStart);
      updated.setHours(Number.isFinite(hours) ? hours : 0, Number.isFinite(minutes) ? minutes : 0, 0, 0);
      setSelectedStart(updated);
      setStartTime(value);
      setAwaitingStartTime(false);

      // If end already exists, notify parent with updated times
      if (selectedEnd) notifyParentRange(updated, selectedEnd);
      return;
    }

    if (type === "end" && selectedEnd) {
      const updated = new Date(selectedEnd);
      updated.setHours(Number.isFinite(hours) ? hours : 0, Number.isFinite(minutes) ? minutes : 0, 59, 999);
      setSelectedEnd(updated);
      setEndTime(value);
      setAwaitingEndTime(false);

      // If start already exists, notify parent with updated times
      if (selectedStart) notifyParentRange(selectedStart, updated);
      return;
    }
  };

  // ----------------- Quick options & clear -----------------
//   const handleQuickSelection = (option) => {
//   const newStartDate = new Date();
//   const newEndDate = new Date();
//   newStartDate.setHours(0, 0, 0, 0);
//   newEndDate.setHours(23, 59, 59, 999);
//   if(isHourTime){
//     switch (option) {
//       case "last15minutes":
//         newStartDate.setMinutes(newStartDate.getMinutes() - 15);
//         break;
//       case "last30minutes":
//         newStartDate.setMinutes(newStartDate.getMinutes() - 30);
//         break;
//       case "last1hours":
//         newStartDate.setHours(newStartDate.getHours() - 1);
//         break;
//       case "last6hours":
//         newStartDate.setHours(newStartDate.getHours() - 6);
//         break;
//       case "last12hours":
//         newStartDate.setHours(newStartDate.getHours() - 12);
//         break;
//       case "last24hours":
//         newStartDate.setHours(newStartDate.getHours() - 24);
//         break;
//       default:
//         return;
//     }
//   }
//   else{
//     switch (option) {
//       case "today":
//         break;
//       case "yesterday":
//         newStartDate.setDate(newStartDate.getDate() - 1);
//         newEndDate.setDate(newEndDate.getDate() - 1);
//         break;
//       case "last7days":
//         newStartDate.setDate(newStartDate.getDate() - 6);
//         break;
//       case "last30days":
//         newStartDate.setDate(newStartDate.getDate() - 29);
//         break;
//       case "thismonth":
//         newStartDate.setDate(1);
//         break;
//       case "lastmonth":
//         newStartDate.setMonth(newStartDate.getMonth() - 1, 1);
//         newEndDate.setMonth(newEndDate.getMonth(), 0); // Last day of previous month
//         break;
//       default:
//         return;
//   }
//   }



//   setSelectedStart(newStartDate);
//   setSelectedEnd(newEndDate);
//   setStartTime(formatTime(newStartDate));
//   setEndTime(formatTime(newEndDate));
//   setShowQuickOptions(false);

//     // Immediately tell parent (quick options finalize instantly)
//     notifyParentRange(newStartDate, newEndDate);
// };
const handleQuickSelection = (option) => {
  const now = new Date();
  let newStartDate = new Date(now);
  let newEndDate = new Date(now);

  if (isHourTime) {
    switch (option) {
      case "last15minutes":
        newStartDate = new Date(now.getTime() - 15 * 60 * 1000);
        break;
      case "last30minutes":
        newStartDate = new Date(now.getTime() - 30 * 60 * 1000);
        break;
      case "last1hours":
        newStartDate = new Date(now.getTime() - 1 * 60 * 60 * 1000);
        break;
      case "last6hours":
        newStartDate = new Date(now.getTime() - 6 * 60 * 60 * 1000);
        break;
      case "last12hours":
        newStartDate = new Date(now.getTime() - 12 * 60 * 60 * 1000);
        break;
      case "last24hours":
        newStartDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
      default:
        return;
    }
  } else {
    // Day-based selections
    newStartDate.setHours(0, 0, 0, 0);
    newEndDate.setHours(23, 59, 59, 999);

    switch (option) {
      case "today":
        break;
      case "yesterday":
        newStartDate.setDate(newStartDate.getDate() - 1);
        newEndDate.setDate(newEndDate.getDate() - 1);
        break;
      case "last7days":
        newStartDate.setDate(newStartDate.getDate() - 6);
        break;
      case "last30days":
        newStartDate.setDate(newStartDate.getDate() - 29);
        break;
      case "thismonth":
      newStartDate.setDate(1);
        break;
      case "lastmonth":
      newStartDate.setMonth(newStartDate.getMonth() - 1, 1);
      newEndDate.setMonth(newEndDate.getMonth(), 0); // Last day of previous month
        break;
      default:
        return;
    }
  }

  setSelectedStart(newStartDate);
  setSelectedEnd(newEndDate);
  setStartTime(formatTime(newStartDate));
  setEndTime(formatTime(newEndDate));
  setShowQuickOptions(false);
  setShowPicker(false);
  setRangeError("");
    // Immediately tell parent (quick options finalize instantly)
  notifyParentRange(newStartDate, newEndDate);
};

  const handleCustomRangeClick = () => {
    setShowQuickOptions(false);
    setShowPicker(true);
    if (!selectedStart) {
      const base = new Date();
      base.setHours(0, 0, 0, 0);
      setSelectedStart(base);
      setSelectedEnd(null);
      setStartTime("");
      setAwaitingStartTime(true);
    }
  };

  const handleClear = () => {
    setSelectedStart(null);
    setSelectedEnd(null);
    setTempEndDate(null);
    setStartTime("");
    setEndTime("");
    setAwaitingStartTime(false);
    setAwaitingEndTime(false);
    setShowQuickOptions(true);
    setRangeError("");
    onDateRangeSelect(null);
  };

  // ----------------- Calendar component -----------------
  const Calendar = () => {
    const daysArray = generateDaysArray(currentDate.getFullYear(), currentDate.getMonth());
    const dayNames = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

    const handleYearChange = (year) => {
      const newDate = new Date(currentDate);
      newDate.setFullYear(year);
      if (newDate <= today) setCurrentDate(newDate);
      else setCurrentDate(new Date(today));
    };

    return (
      <div className="calendar">
        <div className="calendar-header">
          <button onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setCurrentDate((d) => { const nd = new Date(d); nd.setMonth(nd.getMonth() - 1); return nd; }); }}>
            &lt;
          </button>
          <div className="month-year">{currentDate.toLocaleString("default", { month: "long" })}</div>

          <YearDropdown currentYear={currentDate.getFullYear()} onChange={handleYearChange} minYear={MIN_YEAR} maxYear={MAX_YEAR} />

          <button onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setCurrentDate((d) => { const nd = new Date(d); nd.setMonth(nd.getMonth() + 1); return nd; }); }}
            disabled={currentDate.getMonth() === today.getMonth() && currentDate.getFullYear() === today.getFullYear()}>
            &gt;
          </button>
        </div>

        <div className="calendar-grid">
          {dayNames.map((day) => <div key={day} className="day-header">{day}</div>)}

          {daysArray.map((day, i) => {
            if (day === null) return <div key={`empty-${i}`} className="day empty" />;

            const cellDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
            const isStart = selectedStart && isSameDay(cellDate, selectedStart);
            const isEnd = selectedEnd && isSameDay(cellDate, selectedEnd);
            const isFuture = isFutureDate(cellDate);
            const isDisabled = isFuture;
            const isInRange = selectedRange.some((d) => isSameDay(d, cellDate));

            return (
              <div
                key={day}
                role="button"
                tabIndex={0}
                className={`day ${isStart ? "selected-start" : ""} ${isEnd ? "selected-end" : ""} ${isInRange ? "in-range" : ""} ${isDisabled ? "disabled" : ""}`}
                onPointerDown={(e) => {
                  if (isDisabled) return;
                  e.preventDefault();
                  handleDateSelect(cellDate);
                }}
                onPointerEnter={() => !isDisabled && handleDateHover(cellDate)}
              >
                {day}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ----------------- QuickOptions component (same look & feel) -----------------
  const QuickOptions = () => {
    const normalizeStart = (date) => { const d = new Date(date); d.setHours(0, 0, 0, 0); return d; };
    const normalizeEnd = (date) => { const d = new Date(date); d.setHours(23, 59, 59, 999); return d; };

    const isExactRange = (start, end, days) => {
      if (!start || !end) return false;
      const expectedStart = new Date(); expectedStart.setDate(expectedStart.getDate() - (days - 1)); expectedStart.setHours(0, 0, 0, 0);
      const expectedEnd = new Date(); expectedEnd.setHours(23, 59, 59, 999);
      return normalizeStart(start).getTime() === expectedStart.getTime() && normalizeEnd(end).getTime() === expectedEnd.getTime();
    };
    const isYesterday = (start, end) => {
      if (!start || !end) return false;
      const y = new Date(); y.setDate(y.getDate() - 1); y.setHours(0, 0, 0, 0); const ye = new Date(y); ye.setHours(23, 59, 59, 999);
      return normalizeStart(start).getTime() === y.getTime() && normalizeEnd(end).getTime() === ye.getTime();
    };
    const isThisMonth = (start, end) => {
      if (!start || !end) return false;
      const firstDay = new Date(); firstDay.setDate(1); firstDay.setHours(0, 0, 0, 0);
      const lastDay = new Date(); lastDay.setMonth(lastDay.getMonth() + 1, 0); lastDay.setHours(23, 59, 59, 999);
      return normalizeStart(start).getTime() === firstDay.getTime() && normalizeEnd(end).getTime() === lastDay.getTime();
    };
    const isLastMonth = (start, end) => {
      if (!start || !end) return false;
      const f = new Date(); f.setMonth(f.getMonth() - 1, 1); f.setHours(0, 0, 0, 0);
      const l = new Date(); l.setDate(0); l.setHours(23, 59, 59, 999);
      return normalizeStart(start).getTime() === f.getTime() && normalizeEnd(end).getTime() === l.getTime();
    };
    const isCustomRange = (start, end) => start && end && !isExactRange(start, end, 1) && !isYesterday(start, end) && !isExactRange(start, end, 7) && !isExactRange(start, end, 30) && !isThisMonth(start, end) && !isLastMonth(start, end);

    return (
      <div className="quick-options">
      {isHourTime ? (
        <>
          <div className={`quick-option`} onClick={() => handleQuickSelection("last15minutes")}>Last 15 Minutes</div>
          <div className={`quick-option`} onClick={() => handleQuickSelection("last30minutes")}>Last 30 Minutes</div>
          <div className={`quick-option`} onClick={() => handleQuickSelection("last1hours")}>Last 1 Hour</div>
          <div className={`quick-option`} onClick={() => handleQuickSelection("last6hours")}>Last 6 Hours</div>
          <div className={`quick-option`} onClick={() => handleQuickSelection("last12hours")}>Last 12 Hours</div>
          <div className={`quick-option`} onClick={() => handleQuickSelection("last24hours")}>Last 24 Hours</div>
          <div className={`quick-option`} onClick={(e) => { e.stopPropagation(); handleCustomRangeClick(); }}>Custom Range</div>
        </>
      ) : (
        <>
          <div className={`quick-option ${isExactRange(selectedStart, selectedEnd, 1) ? "actived" : ""}`} onClick={() => handleQuickSelection("today")}>Today</div>
         <div className={`quick-option ${isYesterday(selectedStart, selectedEnd) ? "actived" : ""}`} onClick={() => handleQuickSelection("yesterday")}>Yesterday</div>
         <div className={`quick-option ${isExactRange(selectedStart, selectedEnd, 7) ? "actived" : ""}`} onClick={() => handleQuickSelection("last7days")}>Last 7 Days</div>
         <div className={`quick-option ${isExactRange(selectedStart, selectedEnd, 30) ? "actived" : ""}`} onClick={() => handleQuickSelection("last30days")}>Last 30 Days</div>
         <div className={`quick-option ${isThisMonth(selectedStart, selectedEnd) ? "actived" : ""}`} onClick={() => handleQuickSelection("thismonth")}>This Month</div>
         <div className={`quick-option ${isLastMonth(selectedStart, selectedEnd) ? "actived" : ""}`} onClick={() => handleQuickSelection("lastmonth")}>Last Month</div>
         <div className={`quick-option ${isCustomRange(selectedStart, selectedEnd) ? "actived" : ""}`} onClick={(e) => { e.stopPropagation(); handleCustomRangeClick(); }}>Custom Range</div>
         </>
      )}
    </div>
    );
  };

  // ----------------- Render -----------------
  return (
    <div className="date-range-picker" ref={pickerRef}>
      <div
        className={`date-range-input ${selectedStart || showPicker ? "focused" : ""}`}
        onClick={() => { if (!showPicker) setShowPicker(true); /* do not close on re-click — close only on outside click */ }}
      >
        <div className="input-container">
          <label className={`date-range-label ${selectedStart || showPicker ? "shrink" : ""}`}>{label}</label>
          <input
            type="text"
            readOnly
            value={
              selectedStart && selectedEnd
                ? `${formatDate(selectedStart)} - ${formatDate(selectedEnd)}`
                : selectedStart
                ? `${formatDate(selectedStart)} - ...`
                : ""
            }
          />
        </div>

        <div className="icon-container">
          {(selectedStart || selectedEnd) && (
            <span
              className="clear-icon"
              onClick={(e) => { e.stopPropagation(); handleClear(); }}
              title="Clear"
            >
              ×
            </span>
          )}
          <span className="calendar-icon"><EventIcon /></span>
        </div>
      </div>

      {showPicker && (
        <div className="date-range-dropdown" ref={dropdownRef} onMouseDown={(e) => e.stopPropagation()}>
          {(!selectedStart && !selectedEnd) || showQuickOptions ? (
            <QuickOptions />
          ) : (
            <div className="calendar-dd">
              <div className="calendar-with-time">
                <Calendar />
                <div className="time-panel">
                  <div className="time-field">
                    <label>Start Time</label>
                    <input
                      type="time"
                      ref={startTimeRef}
                      disabled={!selectedStart}
                      value={selectedStart ? startTime : ""}
                      onChange={(e) => handleTimeChange("start", e.target.value)}
                      onClick={(e) => e.target.showPicker && e.target.showPicker()} 
                      style={{ cursor: !selectedStart ? "not-allowed" : "pointer" }} 
                    />
                  </div>

                  <div className="time-field">
                    <label>End Time</label>
                    <input
                      type="time"
                      ref={endTimeRef}
                      disabled={!selectedEnd}
                      value={selectedEnd ? endTime : ""}
                      onChange={(e) => handleTimeChange("end", e.target.value)}
                      onClick={(e) => e.target.showPicker && e.target.showPicker()} //  open time picker when input clicked
                      style={{ cursor: !selectedEnd ? "not-allowed" : "pointer" }} //  pointer
                    />
                  </div>
                </div>
              </div>
              <div className="date-range-footer">
                <div className="selected-range-preview">
                  {selectedStart && formatDateTime(selectedStart)}
                  {selectedEnd && ` → ${formatDateTime(selectedEnd)}`}
                </div>
              </div>
              {rangeError && <div className="date-range-error" role="alert">{rangeError}</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

DateRangePicker.propTypes = {
  onDateRangeSelect: PropTypes.func.isRequired,
  initialStartDate: PropTypes.instanceOf(Date),
  initialEndDate: PropTypes.instanceOf(Date),
  isHourTime: PropTypes.bool,
  label: PropTypes.string,
};

export default DateRangePicker;

// YearDropdown unchanged
const YearDropdown = ({ currentYear, onChange, minYear, maxYear }) => {
  const years = Array.from({ length: maxYear - minYear + 1 }, (_, i) => minYear + i);
  return (
    <select value={currentYear} onChange={(e) => onChange(parseInt(e.target.value))} className="year-dropdown">
      {years.map((year) => <option key={year} value={year}>{year}</option>)}
    </select>
  );
};
